/**
 * Add-user email checks: boolean flags → one place for mocks, Storybook, and API mapping.
 *
 * Preset mock addresses (opt-in via usePresetMockEmails on AddUserWizard):
 * - closed@adduser.mock.local
 * - blocked@adduser.mock.local
 * - access@adduser.mock.local → has_access
 * - owner@adduser.mock.local / coowner@adduser.mock.local → valid + inviter hint
 * - employee@adduser.mock.local → valid, already an employee (no employee checkbox)
 * - any other address → valid (available), via getPresetOrAvailableMockState
 */

export const ADD_USER_MOCK_EMAILS = {
  "closed@adduser.mock.local": {
    closed: true,
    blocked: false,
    has_access: false,
    valid: false,
    inviterRole: null,
    usedBy: null,
  },
  "blocked@adduser.mock.local": {
    closed: false,
    blocked: true,
    has_access: false,
    valid: false,
    inviterRole: null,
    usedBy: null,
  },
  "access@adduser.mock.local": {
    closed: false,
    blocked: false,
    has_access: true,
    valid: false,
    inviterRole: null,
    usedBy: null,
  },
  "owner@adduser.mock.local": {
    closed: false,
    blocked: false,
    has_access: false,
    valid: true,
    inviterRole: "owner",
    usedBy: null,
  },
  "coowner@adduser.mock.local": {
    closed: false,
    blocked: false,
    has_access: false,
    valid: true,
    inviterRole: "co-owner",
    usedBy: null,
  },
  "employee@adduser.mock.local": {
    closed: false,
    blocked: false,
    has_access: false,
    valid: true,
    inviterRole: null,
    usedBy: null,
    isEmployee: true,
  },
};

/** @typedef {{ closed: boolean, blocked: boolean, has_access: boolean, valid: boolean, inviterRole: string | null, isEmployee: boolean, allowedInviteRoles: string[] | null }} AddUserEmailValidationState */

export function emptyValidationState() {
  return {
    closed: false,
    blocked: false,
    has_access: false,
    valid: false,
    inviterRole: null,
    usedBy: null,
    isEmployee: false,
    allowedInviteRoles: null,
  };
}

/**
 * Email is OK to use for add-user (not blocked / closed / already on account).
 * Use when API is unavailable, mock table has no entry, or checks succeed with no flags.
 */
export function validAvailableEmailState(inviterRole = null) {
  return {
    closed: false,
    blocked: false,
    has_access: false,
    valid: true,
    inviterRole: inviterRole || null,
    usedBy: null,
    isEmployee: false,
    allowedInviteRoles: null,
  };
}

/**
 * Preset mocks when the address matches {@link ADD_USER_MOCK_EMAILS};
 * otherwise {@link validAvailableEmailState} (any other email can proceed).
 */
export function getPresetOrAvailableMockState(emailLower) {
  const preset = getMockValidationState(emailLower);
  if (preset) return preset;
  return validAvailableEmailState();
}

function normalizeApiUserState(state) {
  if (state == null || state === "") return "";
  return String(state).toLowerCase().trim();
}

/**
 * v1: `user` / `access` as `true` mean on account. v2: non-null id values.
 * Explicit `false` is not a valid id.
 * Empty objects `{}` from serializers must NOT count as "present" (would mark every email as on-account).
 * @param {unknown} v
 * @returns {boolean}
 */
function isPresentUserOrAccessField(v) {
  if (typeof v === "boolean") {
    return v === true;
  }
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    if (t === "" || t === "false" || t === "null" || t === "none" || t === "0") {
      return false;
    }
    return true;
  }
  if (v == null || v === "") {
    return false;
  }
  if (typeof v === "number") {
    return v !== 0;
  }
  if (Array.isArray(v)) {
    return v.length > 0;
  }
  if (typeof v === "object") {
    return Object.keys(v).length > 0;
  }
  return true;
}

/**
 * v2: `employee` is null if unlinked, or an id / object if linked to an employee.
 * Boolean/string `false` must not count as linked (API often sends `employee: false`).
 * @param {unknown} employee
 * @returns {boolean}
 */
function isEmployeeLinked(employee) {
  if (typeof employee === "boolean") {
    return employee === true;
  }
  if (typeof employee === "string") {
    const t = employee.trim().toLowerCase();
    if (t === "" || t === "false" || t === "null" || t === "none") {
      return false;
    }
  }
  return employee != null && employee !== "";
}

/**
 * Display name for "already on account" copy: legacy `used_by` or v2 name fields.
 * @param {Record<string, unknown>} data
 * @returns {string | null}
 */
function formatUsedByFromResponse(data) {
  if (data.used_by != null) {
    return String(data.used_by);
  }
  if (data.employee_name != null && String(data.employee_name).trim() !== "") {
    return String(data.employee_name).trim();
  }
  const a = (data.user_first_name != null && String(data.user_first_name).trim()) || "";
  const b = (data.user_last_name != null && String(data.user_last_name).trim()) || "";
  const name = [a, b].filter(Boolean).join(" ");
  return name || null;
}

/**
 * Map POST test/email API payload to the same shape used by mocks.
 * Extend when the API adds fields (e.g. inviter_role) without breaking callers.
 *
 * - `closed` / `blocked`: `data.closed`, `data.blocked`, legacy `state`, and v2 `user_state`
 *   (e.g. "closed", "blocked").
 * - `has_access`: email linked to an employee (`employee`), or user already on the account
 *   (v1: `user`+`access` true; v2: truthy `user` and `access` ids, including invited/unlinked).
 * - `used_by` / v2: `employee_name` or `user_first_name` + `user_last_name` for display.
 */
export function mapApiResponseToValidationState(data) {
  const s = emptyValidationState();
  if (!data || typeof data !== "object") {
    s.valid = true;
    return s;
  }
  const stateNorm = normalizeApiUserState(data.state);
  const userStateNorm = normalizeApiUserState(data.user_state);
  s.closed = !!data.closed || stateNorm === "closed" || userStateNorm === "closed";
  s.blocked = !!data.blocked || stateNorm === "blocked" || userStateNorm === "blocked";
  s.usedBy = formatUsedByFromResponse(data);

  const onAccountWithAccess =
    isPresentUserOrAccessField(data.user) && isPresentUserOrAccessField(data.access);
  const isEmployee = isEmployeeLinked(data.employee);
  // With the invite options present (idea-MDP-621 on) an employee who holds no
  // Access can still be invited as an administrator, so employment alone no
  // longer blocks the wizard; the access step then hides the employee checkbox.
  const employeeBlocks = isEmployee && !Array.isArray(data.allowed_invite_roles);

  if (s.closed || s.blocked) {
    s.has_access = false;
  } else if ("has_access" in data) {
    const ha = data.has_access;
    if (ha == null) {
      s.has_access = !!(employeeBlocks || onAccountWithAccess);
    } else if (typeof ha === "boolean") {
      s.has_access = ha;
    } else if (typeof ha === "string") {
      const t = ha.trim().toLowerCase();
      s.has_access = t === "true" || t === "1" || t === "yes";
    } else if (typeof ha === "number") {
      s.has_access = ha !== 0;
    } else {
      s.has_access = !!(employeeBlocks || onAccountWithAccess);
    }
  } else {
    s.has_access = !!(employeeBlocks || onAccountWithAccess);
  }

  const invalid = s.closed || s.blocked || s.has_access;
  s.valid = !invalid;
  s.isEmployee = isEmployee;
  if (Array.isArray(data.allowed_invite_roles)) {
    s.allowedInviteRoles = data.allowed_invite_roles.map((r) =>
      String(r).toLowerCase()
    );
  }
  if (data.inviter_role != null) {
    const r = String(data.inviter_role).toLowerCase();
    if (r === "co-owner" || r === "co_owner" || r === "coowner") {
      s.inviterRole = "co-owner";
    } else if (r === "owner") {
      s.inviterRole = "owner";
    }
  }
  return s;
}

export function getMockValidationState(emailLower) {
  if (!emailLower || typeof emailLower !== "string") return null;
  const key = emailLower.trim().toLowerCase();
  const preset = ADD_USER_MOCK_EMAILS[key];
  if (!preset) return null;
  return { ...preset };
}

/**
 * Interpret boolean flags → wizard routing (single source of truth).
 * Priority: closed/blocked (combined UI) → has_access → valid.
 */
export function interpretEmailValidationState(state) {
  if (!state) {
    return {
      kind: "ok",
      inviterRole: null,
      usedBy: null,
    };
  }
  if (state.closed || state.blocked) {
    return { kind: "blocked_or_closed", inviterRole: null, usedBy: null };
  }
  if (state.has_access) {
    return {
      kind: "has_access",
      inviterRole: null,
      usedBy: state.usedBy || null,
    };
  }
  /* Defensive: no blocking flags but valid false — still allow the wizard to proceed */
  return {
    kind: "ok",
    inviterRole: state.inviterRole || null,
    usedBy: null,
    isEmployee: !!state.isEmployee,
    allowedInviteRoles: state.allowedInviteRoles || null,
  };
}

/**
 * Normalize legacy handler results: { ok }, { blocked }, { closed }, { inUse }, etc.
 */
export function normalizeHandlerResultToValidationState(result) {
  if (!result || typeof result !== "object") {
    return { ...emptyValidationState(), valid: true };
  }
  const hasStructuredFlags =
    "has_access" in result ||
    "valid" in result ||
    "closed" in result ||
    "blocked" in result;
  if (hasStructuredFlags) {
    const s = emptyValidationState();
    const stateNorm = normalizeApiUserState(result.state);
    const userStateNorm = normalizeApiUserState(result.user_state);
    s.closed = !!result.closed || stateNorm === "closed" || userStateNorm === "closed";
    s.blocked = !!result.blocked || stateNorm === "blocked" || userStateNorm === "blocked";
    s.has_access = !!result.has_access;
    if (s.closed || s.blocked || s.has_access) {
      s.valid = false;
    } else {
      s.valid = result.valid !== false;
    }
    s.inviterRole = result.inviterRole ?? null;
    s.usedBy = result.usedBy != null ? String(result.usedBy) : null;
    s.isEmployee = !!result.isEmployee;
    s.allowedInviteRoles = result.allowedInviteRoles ?? null;
    return s;
  }
  if (result.inUse) {
    const s = { ...emptyValidationState(), has_access: true };
    if (result.usedBy != null) s.usedBy = String(result.usedBy);
    return s;
  }
  if (result.blocked) {
    return { ...emptyValidationState(), blocked: true };
  }
  if (result.closed) {
    return { ...emptyValidationState(), closed: true };
  }
  if (result.ok) {
    const s = { ...emptyValidationState(), valid: true };
    if (result.inviterRole != null) s.inviterRole = result.inviterRole;
    return s;
  }
  return { ...emptyValidationState(), valid: true };
}
